import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import chat01 from "../assets/chat-reviews/chat-01.webp";
import chat02 from "../assets/chat-reviews/chat-02.webp";
import chat03 from "../assets/chat-reviews/chat-03.webp";
import chat04 from "../assets/chat-reviews/chat-04.webp";
import chat05 from "../assets/chat-reviews/chat-05.webp";
import chat06 from "../assets/chat-reviews/chat-06.webp";
import chat07 from "../assets/chat-reviews/chat-07.webp";
import chat08 from "../assets/chat-reviews/chat-08.webp";
import chat09 from "../assets/chat-reviews/chat-09.webp";
import chat10 from "../assets/chat-reviews/chat-10.webp";
import chat11 from "../assets/chat-reviews/chat-11.webp";
import chat12 from "../assets/chat-reviews/chat-12.webp";
import chat13 from "../assets/chat-reviews/chat-13.webp";
import chat14 from "../assets/chat-reviews/chat-14.webp";

const REVIEWS = [
  chat01, chat02, chat03, chat04, chat05, chat06, chat07,
  chat08, chat09, chat10, chat11, chat12, chat13, chat14,
];

export default function ChatReviews() {
  const trackRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0 });

  const nudge = (dir) =>
    trackRef.current?.scrollBy({ left: dir * 340, behavior: "smooth" });

  const onPointerDown = (e) => {
    const t = trackRef.current;
    if (!t) return;
    drag.current = { down: true, startX: e.clientX, startScroll: t.scrollLeft };
  };
  const onPointerMove = (e) => {
    const t = trackRef.current;
    if (!t || !drag.current.down) return;
    t.scrollLeft = drag.current.startScroll - (e.clientX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
  };

  return (
    <div className="mt-16 md:mt-20" data-testid="chat-reviews">
      <div className="flex items-end justify-between gap-6 mb-8 md:mb-10">
        <div>
          <span className="eyebrow" data-testid="chat-reviews-eyebrow">
            <span className="eyebrow-dot" />
            Chat reviews
          </span>
          <h3 className="mt-4 font-semibold tracking-tight text-2xl md:text-4xl leading-[1.1]">
            <span className="text-gradient">Straight from our</span>{" "}
            <span className="text-gradient-dim">client chats.</span>
          </h3>
        </div>
        <div className="hidden md:flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            aria-label="Previous reviews"
            data-testid="chat-reviews-prev"
            onClick={() => nudge(-1)}
            className="w-11 h-11 rounded-full flex items-center justify-center bg-white border border-black/10 shadow-[0_10px_24px_-10px_rgba(10,10,10,0.25)] hover:scale-110 hover:border-[#b98d34] transition-all"
          >
            <ChevronLeft className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2} />
          </button>
          <button
            type="button"
            aria-label="Next reviews"
            data-testid="chat-reviews-next"
            onClick={() => nudge(1)}
            className="w-11 h-11 rounded-full flex items-center justify-center bg-white border border-black/10 shadow-[0_10px_24px_-10px_rgba(10,10,10,0.25)] hover:scale-110 hover:border-[#b98d34] transition-all"
          >
            <ChevronRight className="w-5 h-5 text-[#0a0a0a]" strokeWidth={2} />
          </button>
        </div>
      </div>

      <div className="relative -mx-5 md:-mx-8">
        {/* edge fades */}
        <div
          aria-hidden
          className="absolute left-0 top-0 bottom-0 w-10 md:w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(90deg, #f3f1eb 0%, transparent 100%)" }}
        />
        <div
          aria-hidden
          className="absolute right-0 top-0 bottom-0 w-10 md:w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(270deg, #f3f1eb 0%, transparent 100%)" }}
        />

        <div
          ref={trackRef}
          data-testid="chat-reviews-track"
          className="no-scrollbar flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory cursor-grab active:cursor-grabbing select-none px-5 md:px-8 py-1"
          style={{ scrollbarWidth: "none" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          {REVIEWS.map((src, i) => (
            <motion.div
              key={i}
              data-testid={`chat-review-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: Math.min(i * 0.05, 0.4), ease: [0.65, 0, 0.35, 1] }}
              className="snap-start shrink-0 w-[230px] md:w-[290px] aspect-[4/5] rounded-2xl overflow-hidden border border-black/10"
              style={{
                background: "#0a0a0a",
                boxShadow: "0 24px 50px -20px rgba(10,10,10,0.30)",
              }}
            >
              <img
                src={src}
                alt={`Client chat review ${i + 1}`}
                loading="lazy"
                draggable={false}
                className="w-full h-full object-cover pointer-events-none"
              />
            </motion.div>
          ))}
        </div>
      </div>

      <p className="mt-5 text-center text-[#0a0a0a]/40 text-[11px] tracking-[0.18em] uppercase md:hidden">
        Swipe to browse
      </p>
    </div>
  );
}
